import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import academyIndexHelpers from '../../../helpers/academyIndexHelpers';

import { FiltersListProps } from '../../../types/components/academy/sidebarNav/LeftNavBarFilters';
import { ArticleFilterConfig } from '../../../types/api/AcademyTypes';

import { CheckboxCheckedIcon, CheckboxEmptyIcon, MinusIcon, PlusIcon } from '../../../modules/Icons';

const FiltersList: React.FC<FiltersListProps> = ({
  selectedFilters,
  setSelectedFilters,
  showScroll,
  disabled,
}) => {
  const dispatch = useDispatch();
  const { getFilters } = academyIndexHelpers;

  const [expandedSections, setExpandedSections] = useState<ArticleFilterConfig[]>([]);
  const [filtersConfig, setFiltersConfig] = useState<ArticleFilterConfig[]>([]);

  const retrieveFilters = async () => {
    const filters = await getFilters(dispatch);
    setFiltersConfig(filters);
    setExpandedSections([filters[0]]); // auto-expand first filter
  }

  useEffect(() => {
    retrieveFilters();
  }, []);

  const handleFilterHeaderClick = (section: ArticleFilterConfig) => {
    if (disabled) return;
    sectionIsExpanded(section.facetKey) ?
      setExpandedSections([...expandedSections.filter(es => es.facetKey !== section.facetKey) ]) :
      setExpandedSections([ ...expandedSections, section ]);
  }

  const handleFilterListItemClick = (facetKey: string, option: string) => {
    if (disabled) return;
    const currentFilters = selectedFilters[facetKey] || [];
    optionIsSelected(facetKey, option) ?
      setSelectedFilters({
        ...selectedFilters,
        [facetKey]: [ ...currentFilters.filter((o: string) => o !== option) || [] ]
      }) :
      setSelectedFilters({
        ...selectedFilters,
        [facetKey]: [ ...currentFilters, option ]
      });
  };

  const sectionIsExpanded = (facetKey: string): boolean =>
    !!expandedSections.map(es => es.facetKey).find(fk => fk === facetKey);

  const optionIsSelected = (sectionKey: string, option: string): boolean => !!selectedFilters[sectionKey]?.includes(option);

  const renderFilterSection = (sectionConfig: ArticleFilterConfig): React.ReactElement => {
    const { facetKey, name, values } = sectionConfig;
    return (
      <li className="block pb-2" key={facetKey} cy-test-id={`filter-group-${facetKey}`}>
        <div
          className="h-12 flex items-center text-[16px] text-white leading-[48px] tracking-wide uppercase gap-2 font-medium w-full cursor-pointer"
          onClick={() => handleFilterHeaderClick(sectionConfig)}
          cy-test-id="expand-toggle"
        >
          {name}
          <div className="flex items-center justify-center ml-auto align-middle">
            {!!selectedFilters[facetKey]?.length && (
              <div className="bg-a-blue text-white rounded-[1px] text-[12px] lg:px-[5px] flex items-center justify-center h-[30px] w-[20px]">
                {selectedFilters[facetKey].length}
              </div>
            )}
            <button className="p-2" data-filter={sectionConfig.facetKey}>
              {!!sectionIsExpanded(sectionConfig.facetKey) ? (
                <MinusIcon data-filter={sectionConfig.facetKey} />
              ) : (
                <PlusIcon data-filter={sectionConfig.facetKey} />
              )}
            </button>
          </div>
        </div>
        <ul className={!!sectionIsExpanded(sectionConfig.facetKey) ? 'block' : 'hidden'}>
          {values.map(option => (
            <li
              className={`text-[16px] flex gap-[15px] items-center align-middle h-12 text-white-60 font-normal hover:text-a-blue leading-[48px] ${disabled ? '' : 'cursor-pointer'}`}
              key={option}
              onClick={() => handleFilterListItemClick(facetKey, option)}
              cy-test-id={`${facetKey}-option-${option}`}
            >
              {optionIsSelected(facetKey, option) ? <CheckboxCheckedIcon /> : <CheckboxEmptyIcon />}
              <span
                className={
                  optionIsSelected(facetKey, option)
                    ? `text-a-blue`
                    : `text-white-60 ${disabled ? '' : 'hover:text-a-blue'}`
                }
              >
                {option}
              </span>
            </li>
          ))}
        </ul>
      </li>
    )
  };

  return (
    <div cy-test-id="filters-list" className={`max-h-[calc(100vh-5px)] ${showScroll && 'overflow-y-auto scrollbar-custom'}`}>
      <ul className={`px-4 ${disabled ? 'opacity-[0.6]' : ''}`}>
        {filtersConfig.map(sectionConfig => renderFilterSection(sectionConfig))}
      </ul>
    </div>
  );
};

export default FiltersList;
